import { Star, StarSolid } from "@ignite-analytics/icons";
import { Box, Button, ButtonBase, FormControlLabel, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import type { InputProps } from "../answers";

import { optionSx } from "./optionSx";

const NOT_APPLICABLE = "_na";

const StarIcon: React.FC<{ fill: number }> = ({ fill }) => (
    <Box sx={{ position: "relative", display: "flex", fontSize: 28 }}>
        <Star fontSize="inherit" sx={{ color: "grey.400" }} />
        <Box sx={{ position: "absolute", inset: 0, width: `${fill * 100}%`, overflow: "hidden", display: "flex" }}>
            <StarSolid fontSize="inherit" sx={{ color: "#FFB400", flex: "none" }} />
        </Box>
    </Box>
);

export const RatingInput: React.FC<InputProps<"rating">> = ({ question, answer, onChange }) => {
    const { formatMessage } = useIntl();
    const [hover, setHover] = useState<number>();
    const step = question.ratingStep ?? 1;
    const value = typeof answer?.value === "number" ? answer.value : undefined;
    const notApplicable = answer?.value === NOT_APPLICABLE;
    const shown = hover ?? value;
    const pick = (picked: number | typeof NOT_APPLICABLE | undefined) => onChange({ ...answer, value: picked });
    const listed = !!question.scale?.length;
    const stars = !listed || step === 0.5;
    const notApplicableButton = question.allowNotApplicable && (
        <Button
            size="small"
            color={notApplicable ? "ghostPrimary" : "ghostGray"}
            aria-pressed={notApplicable}
            onClick={() => pick(notApplicable ? undefined : NOT_APPLICABLE)}
            sx={{ alignSelf: "flex-start" }}
        >
            {formatMessage({ defaultMessage: "N/A" })}
        </Button>
    );

    return (
        <Stack spacing={1}>
            {stars && (
                <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                    <Stack
                        direction="row"
                        role="radiogroup"
                        spacing={0.5}
                        onMouseLeave={() => setHover(undefined)}
                        sx={{ opacity: notApplicable ? 0.5 : 1 }}
                    >
                        {Array.from({ length: question.ratingMax }, (_, i) => i + 1).map((star) => (
                            <Box key={star} sx={{ position: "relative", display: "flex" }}>
                                <StarIcon fill={Math.min(Math.max((shown ?? 0) - star + 1, 0), 1)} />
                                <Stack direction="row" sx={{ position: "absolute", inset: 0 }}>
                                    {(step === 0.5 ? [star - 0.5, star] : [star]).map((option) => (
                                        <ButtonBase
                                            key={option}
                                            role="radio"
                                            aria-checked={value === option}
                                            aria-label={String(option)}
                                            onMouseEnter={() => setHover(option)}
                                            onFocus={() => setHover(option)}
                                            onBlur={() => setHover(undefined)}
                                            onClick={() => pick(value === option ? undefined : option)}
                                            sx={{ flex: 1, borderRadius: 0.5 }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                    {notApplicableButton}
                </Stack>
            )}
            {listed && (
                <RadioGroup
                    sx={{ gap: 0.5 }}
                    value={value === undefined ? "" : String(value)}
                    onChange={(event) => pick(Number(event.target.value))}
                >
                    {question.scale?.map((level, i) => (
                        <FormControlLabel
                            key={i}
                            sx={optionSx}
                            value={String(i + 1)}
                            control={
                                <Radio
                                    checked={
                                        value !== undefined && Math.floor(value) <= i + 1 && i + 1 <= Math.ceil(value)
                                    }
                                    onClick={() => value === i + 1 && pick(undefined)}
                                />
                            }
                            label={
                                <Stack>
                                    <Typography variant="textSm" fontWeight={600}>
                                        {i + 1} {level.label}
                                    </Typography>
                                    {level.description && (
                                        <Typography variant="textXs" color="textTextHelper">
                                            {level.description}
                                        </Typography>
                                    )}
                                </Stack>
                            }
                        />
                    ))}
                </RadioGroup>
            )}
            {!stars && notApplicableButton}
        </Stack>
    );
};
